import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import GenericTable, {
  GenericTableColumn,
} from "../components/GenericTable";
import PageSectionCard from "../components/PageSectionCard";
import Pagination from "../components/Pagination";
import { useAppSession } from "../context/AppSessionContext";
import { readErrorMessage } from "../lib/http";
import {
  EmployeeMissingName,
  getEmployeeClientCompany,
} from "../types/employees";
import { PaginatedResponse } from "../types/pagination";

const defaultPageSize = 20;

function EmployeesMissingNamePage() {
  const { authorizedFetch, canMaintainEmployees } = useAppSession();

  const [employees, setEmployees] = useState<EmployeeMissingName[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [acceptingId, setAcceptingId] = useState<string | null>(null);
  const [isAcceptingAll, setIsAcceptingAll] = useState(false);
  const [rowError, setRowError] = useState<string | null>(null);

  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const load = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);

    const query = new URLSearchParams({
      page: String(page),
      limit: String(defaultPageSize),
    });

    try {
      const response = await authorizedFetch(
        `/employees/missing-name?${query.toString()}`,
      );

      if (!response.ok) {
        throw new Error(
          await readErrorMessage(
            response,
            `Failed to load employees (${response.status}).`,
          ),
        );
      }

      const payload =
        (await response.json()) as PaginatedResponse<EmployeeMissingName>;
      const responseTotalPages = payload.totalPages ?? 0;

      if (responseTotalPages > 0 && page > responseTotalPages) {
        setPage(responseTotalPages);
        return;
      }

      setEmployees(payload.data ?? []);
      setTotal(payload.total ?? 0);
      setTotalPages(responseTotalPages);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to load employees.";
      setLoadError(message);
      setEmployees([]);
      setTotal(0);
      setTotalPages(0);
    } finally {
      setIsLoading(false);
    }
  }, [authorizedFetch, page]);

  useEffect(() => {
    void load();
  }, [load]);

  const acceptSuggestion = async (employee: EmployeeMissingName) => {
    if (!employee.suggestion) {
      return;
    }

    setAcceptingId(employee.id);
    setRowError(null);

    try {
      const response = await authorizedFetch(
        `/employees/${employee.id}/accept-name-suggestion`,
        { method: "POST" },
      );

      if (!response.ok) {
        throw new Error(
          await readErrorMessage(
            response,
            `Failed to accept suggestion (${response.status}).`,
          ),
        );
      }

      toast.success(
        `Saved "${employee.suggestion.fullName}" for ${employee.nationalId}.`,
      );
      // The record now has a name, so it drops off this list — reload.
      await load();
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to accept suggestion.";
      setRowError(message);
    } finally {
      setAcceptingId(null);
    }
  };

  const acceptAll = async () => {
    setIsAcceptingAll(true);
    setRowError(null);

    try {
      const response = await authorizedFetch(
        `/employees/accept-name-suggestions`,
        { method: "POST" },
      );

      if (!response.ok) {
        throw new Error(
          await readErrorMessage(
            response,
            `Failed to accept suggestions (${response.status}).`,
          ),
        );
      }

      const summary = (await response.json()) as {
        total: number;
        updated: number;
        withoutMatch: number;
      };

      toast.success(
        summary.withoutMatch > 0
          ? `Filled in ${summary.updated} name${summary.updated === 1 ? "" : "s"}. ${summary.withoutMatch} left with no registry match.`
          : `Filled in ${summary.updated} name${summary.updated === 1 ? "" : "s"}.`,
      );
      // Filled records drop off this list; go back to page 1 and reload.
      setPage(1);
      await load();
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to accept suggestions.";
      setRowError(message);
    } finally {
      setIsAcceptingAll(false);
    }
  };

  const showEmptyState = !isLoading && !loadError && employees.length === 0;

  const canAcceptAll =
    canMaintainEmployees && !isLoading && total > 0 && acceptingId === null;

  const columns: GenericTableColumn<EmployeeMissingName>[] = [
    {
      id: "nationalId",
      header: "National ID",
      cell: (employee) => employee.nationalId,
    },
    {
      id: "clientCompany",
      header: "Client Company",
      cell: (employee) => getEmployeeClientCompany(employee)?.name ?? "-",
    },
    {
      id: "workstation",
      header: "Workstation",
      cell: (employee) => employee.workstation?.name ?? "-",
    },
    {
      id: "suggestion",
      header: "Suggested name",
      cell: (employee) =>
        employee.suggestion ? (
          employee.suggestion.fullName
        ) : (
          <span className="hintText">No registry match</span>
        ),
    },
    {
      id: "actions",
      header: "Actions",
      headerClassName: "actionsColumn",
      cellClassName: "actionsColumn",
      cell: (employee) => (
        <div className="headerActions">
          {canMaintainEmployees ? (
            <button
              className="primaryButton tableActionButton"
              type="button"
              disabled={
                !employee.suggestion ||
                acceptingId === employee.id ||
                isAcceptingAll
              }
              onClick={() => void acceptSuggestion(employee)}
            >
              {acceptingId === employee.id ? "Saving..." : "Accept"}
            </button>
          ) : (
            <span className="secondaryButton tableActionButton disabledAction">
              Accept
            </span>
          )}

          {canMaintainEmployees ? (
            <Link
              className="secondaryButton tableActionButton"
              to={`/employees/${employee.id}/edit`}
            >
              Edit
            </Link>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <PageSectionCard
      titleId="employees-missing-name-title"
      eyebrow="Employees"
      title="Employees missing a name"
      lead="Records with a blank name, matched against the National Registry by kennitala. Accept a suggestion to fill in the name."
      actions={
        <div className="headerActions">
          {canMaintainEmployees ? (
            <button
              className="primaryButton"
              type="button"
              disabled={!canAcceptAll || isAcceptingAll}
              onClick={() => void acceptAll()}
            >
              {isAcceptingAll ? "Accepting all..." : "Accept all"}
            </button>
          ) : (
            <span className="primaryButton disabledAction">Accept all</span>
          )}
        </div>
      }
    >
      {isLoading ? <p>Loading employees...</p> : null}

      {loadError ? (
        <p className="errorMessage" role="alert">
          {loadError}
        </p>
      ) : null}

      {rowError ? (
        <p className="errorMessage" role="alert">
          {rowError}
        </p>
      ) : null}

      {showEmptyState ? (
        <div className="emptyState">
          <p>No employees are missing a name.</p>
        </div>
      ) : null}

      {!isLoading && !loadError && employees.length > 0 ? (
        <GenericTable
          ariaLabel="Employees missing a name"
          columns={columns}
          rows={employees}
          rowKey={(employee) => employee.id}
        />
      ) : null}

      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        disabled={isLoading}
        summary={`${total} employee${total === 1 ? "" : "s"} missing a name`}
      />
    </PageSectionCard>
  );
}

export default EmployeesMissingNamePage;
